import { Injectable, inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { AuthRealmService } from '../services/auth-realm.service';
import { ToastService } from '../../shared/services/toast.service';
import { ErrorTrackerService } from '../services/error-tracker/error-tracker.service';
import { LanguageService } from '../services/language.service';
import { HttpStatusCode } from '../../shared/enums/http-status-codes.enum';

class SseHandledError extends Error {}

class SseUnauthorizedError extends SseHandledError {
  constructor() {
    super('Unauthorized');
  }
}

class SseHttpError extends SseHandledError {
  constructor(status: number) {
    super(`HTTP_${status}`);
  }
}

@Injectable({ providedIn: 'root' })
export class SseFetchInterceptorService {
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly errorTrackerService = inject(ErrorTrackerService);
  private readonly languageService = inject(LanguageService);

  private refreshInFlight: Promise<string> | null = null;

  async fetch(url: string, controller: AbortController): Promise<Response> {
    try {
      const token = await this.ensureToken();

      const response = await this.executeRequest(url, token, controller);

      return await this.validateResponse(url, response, controller);
    } catch (error) {
      if (this.isAbortError(error) || error instanceof SseHandledError) {
        throw error;
      }

      this.handleNetworkError(error, url);
      throw error;
    }
  }

  private async executeRequest(url: string, token: string, controller: AbortController): Promise<Response> {
    return fetch(url, {
      headers: this.buildHeaders(token),
      signal: controller.signal,
    });
  }

  private async validateResponse(url: string, response: Response, controller: AbortController): Promise<Response> {
    // 401/403 -> Refresh and retry
    if (this.isAuthError(response)) {
      return this.retryAfterRefresh(url, controller);
    }

    // Other http error
    if (!response.ok) {
      this.handleHttpError(response, url);
    }

    return response;
  }

  private async retryAfterRefresh(url: string, controller: AbortController): Promise<Response> {
    const token = await this.ensureToken(true);

    const response = await this.executeRequest(url, token, controller);

    if (this.isAuthError(response)) {
      this.showUnauthorizedToast();
      this.authService.logout();
      throw new SseUnauthorizedError();
    }

    // Other http error
    if (!response.ok) {
      this.handleHttpError(response, url);
    }

    return response;
  }

  private buildHeaders(token: string): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
      'X-Realm': AuthRealmService.getRealm(),
    };

    const locale = this.languageService.getCurrentLocale();
    if (locale) {
      headers['Accept-Language'] = locale;
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      headers['Time-Zone-ID'] = timeZone;
    }

    return headers;
  }

  private handleHttpError(response: Response, url: string): never {
    const status = response.status;
    const message = this.resolveMessage(status);

    this.errorTrackerService.captureMessage({
      message,
      captureContext: {
        level: 'error',
        extra: {
          url,
          status,
          type: 'SSE_HTTP_ERROR',
        },
      },
    });

    this.toastService.showError({
      summary: `${message}\n${status}`,
    });

    throw new SseHttpError(status);
  }

  private handleNetworkError(error: unknown, url: string): void {
    const message = this.languageService.getInstant('shared.requestInterceptorService.techError');

    this.errorTrackerService.captureMessage({
      message,
      captureContext: {
        level: 'error',
        extra: {
          url,
          type: 'SSE_NETWORK_ERROR',
          originalError: error,
        },
      },
    });

    this.toastService.showError({
      summary: message,
    });
  }

  private isAuthError(response: Response): boolean {
    return response.status === HttpStatusCode.UNAUTHORIZED || response.status === HttpStatusCode.FORBIDDEN;
  }

  private isAbortError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'AbortError';
    }

    return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
  }

  private showUnauthorizedToast(): void {
    const message = this.languageService.getInstant('shared.requestInterceptorService.techErrorInvalidNoAuthorization');

    this.toastService.showError({ summary: message });
  }

  private resolveMessage(status: number): string {
    if (status === HttpStatusCode.BAD_REQUEST) {
      return this.languageService.getInstant('shared.requestInterceptorService.techErrorInvalidRequest');
    }

    return this.languageService.getInstant('shared.requestInterceptorService.techError');
  }

  private ensureToken(forceRefresh = false): Promise<string> {
    if (!forceRefresh && !this.authService.isTokenExpired()) {
      return Promise.resolve(this.authService.token);
    }

    this.refreshInFlight ??= this.authService.updateToken(0).finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }
}
